import { useDeferredValue, useEffect, useMemo, useState, startTransition } from "react"

import {
  apiClient,
  parseOperatorIncidentStreamEvent,
  type AuthorityJurisdictionSummary,
  type OperatorBreakdownMetrics,
  type OperatorIncidentRecord,
  type OperatorPermissions,
  type OperatorPerformanceMetrics,
  type OperatorResponseTimesRecord,
} from "@/lib/api/client"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type {
  OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"
import {
  canAccessAuthorityWorkspace,
  resolveOperatorSession,
} from "@/features/operator/shared/operator-access"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  useOptionalOperatorShellSession,
} from "@/features/operator/shared/operator-shell-session-context"

const AUTHORITY_INCIDENT_STATUSES = [
  "all",
  "reported",
  "triaged",
  "dispatched",
  "active",
  "resolved",
  "closed",
] as const

const AUTHORITY_SEVERITIES = [
  "all",
  "critical",
  "severe",
  "moderate",
  "minor",
] as const

const AUTHORITY_REPORT_KINDS = ["all", "incident", "service_request", "behavior"] as const

export interface AuthorityDeskSearch {
  incidentStatus?: (typeof AUTHORITY_INCIDENT_STATUSES)[number]
  jurisdictionId?: string
  q?: string
  severity?: (typeof AUTHORITY_SEVERITIES)[number]
}

export interface AuthorityReportsSearch {
  incidentStatus?: (typeof AUTHORITY_INCIDENT_STATUSES)[number]
  q?: string
  reportKind?: (typeof AUTHORITY_REPORT_KINDS)[number]
}

interface AuthorityWorkspaceProps {
  incidentId?: number
  mode: "desk" | "reports"
  onSearchChange: (next: Partial<AuthorityDeskSearch & AuthorityReportsSearch>) => void
  onSelectIncident: (incidentId: number) => void
  search: AuthorityDeskSearch | AuthorityReportsSearch
}

interface AuthorityCoreBundle {
  breakdown: OperatorBreakdownMetrics | null
  incidents: OperatorIncidentRecord[]
  jurisdictions: AuthorityJurisdictionSummary[]
  performance: OperatorPerformanceMetrics | null
  responseTimes: OperatorResponseTimesRecord[]
  scope: AuthorityWorkspaceScope
  stats: {
    by_incident_status?: Array<{ _count: number; incidentStatus: string }>
    by_report_kind?: Array<{ _count: number; reportKind: string }>
    recent_24h?: number
    resolved?: number
    total?: number
  } | null
}

interface AuthorityDetailBundle {
  detail: OperatorIncidentRecord
  evidence: {
    auditPackage?: Record<string, unknown> | null
    checksum?: string | null
    incidentId: number
  } | null
  timeline: Array<{
    [key: string]: unknown
    data?: Record<string, unknown> | null
    id?: string
    kind?: string | null
  }>
}

interface AuthorityWorkspaceScope {
  kind: "authority" | "global"
  actorLabel: string
  organizationId?: string
  organizationName?: string | null
  permissions: OperatorPermissions
  updatedBy: string
}

function hasAnalyticsAccess(permissions: OperatorPermissions | null | undefined) {
  return Boolean(permissions?.viewAnalytics)
}

function normalizeToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

function titleizeToken(value: string | null | undefined) {
  const normalized = normalizeToken(value)

  if (!normalized) {
    return "Unknown"
  }

  return normalized
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatFileSize(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return null
  }

  if (value < 1024) {
    return `${value} B`
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function formatPreferredLanguage(value: string | null | undefined) {
  if (value === "hi") {
    return "Hindi (हिन्दी)"
  }

  if (value === "ar") {
    return "Arabic (العربية)"
  }

  if (value === "en") {
    return "English"
  }

  return "Not specified"
}

function formatRelativeTime(value: string | null | undefined) {
  if (!value) {
    return "unknown time"
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  const delta = parsed.getTime() - Date.now()
  const absoluteMinutes = Math.max(Math.round(Math.abs(delta) / 60000), 1)

  if (absoluteMinutes < 60) {
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
      delta < 0 ? -absoluteMinutes : absoluteMinutes,
      "minute"
    )
  }

  const absoluteHours = Math.round(absoluteMinutes / 60)

  return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
    delta < 0 ? -absoluteHours : absoluteHours,
    "hour"
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function readPath(source: unknown, path: string) {
  let current: unknown = source

  for (const segment of path.split(".")) {
    if (!isRecord(current) || !(segment in current)) {
      return undefined
    }

    current = current[segment]
  }

  return current
}

function readFirstString(sources: unknown[], paths: string[]) {
  for (const source of sources) {
    for (const path of paths) {
      const value = readPath(source, path)

      if (typeof value === "string" && value.trim()) {
        return value.trim()
      }
    }
  }

  return null
}

function safeJsonStringify(value: unknown) {
  try {
    return JSON.stringify(value ?? {}, null, 2) ?? "{}"
  } catch {
    return "[unserializable payload]"
  }
}

function formatTimelineStatus(
  previousStatus: string | null,
  nextStatus: string | null
) {
  if (previousStatus && nextStatus) {
    if (normalizeToken(previousStatus) === normalizeToken(nextStatus)) {
      return titleizeToken(nextStatus)
    }

    return `${titleizeToken(previousStatus)} -> ${titleizeToken(nextStatus)}`
  }

  if (nextStatus) {
    return titleizeToken(nextStatus)
  }

  if (previousStatus) {
    return titleizeToken(previousStatus)
  }

  return null
}

function formatTimelineActor(actorName: string | null, actorType: string | null) {
  if (actorName && actorType) {
    return `${actorName} (${titleizeToken(actorType)})`
  }

  if (actorName) {
    return actorName
  }

  if (actorType) {
    return titleizeToken(actorType)
  }

  return null
}

function formatTimelineArea(
  jurisdictionName: string | null,
  stationName: string | null
) {
  if (jurisdictionName && stationName) {
    return `${jurisdictionName} • ${stationName}`
  }

  return jurisdictionName ?? stationName
}

function describeTimelineEntry(entry: AuthorityDetailBundle["timeline"][number]) {
  const root = isRecord(entry) ? entry : {}
  const payload = isRecord(root.data) ? root.data : null
  const sources = [root, payload]
  const payloadSources = [payload, root]
  const previousStatus = readFirstString(payloadSources, [
    "fromStatus",
    "previousStatus",
    "oldStatus",
    "statusFrom",
  ])
  const nextStatus = readFirstString(payloadSources, [
    "toStatus",
    "newStatus",
    "statusTo",
    "incidentStatus",
    "status",
  ])

  return {
    actor: formatTimelineActor(
      readFirstString(payloadSources, [
        "actor.fullName",
        "actor.name",
        "actor.label",
        "actorName",
        "authority.fullName",
        "authorityName",
        "updatedBy",
        "createdBy",
        "performedBy",
        "user.name",
      ]),
      readFirstString(payloadSources, [
        "actor.type",
        "actor.actorType",
        "actorType",
        "updatedByType",
        "createdByType",
        "authorityType",
        "user.role",
      ])
    ),
    area: formatTimelineArea(
      readFirstString(payloadSources, [
        "jurisdiction.name",
        "jurisdictionName",
        "jurisdiction",
      ]),
      readFirstString(payloadSources, [
        "station.name",
        "stationName",
        "station",
      ])
    ),
    eventLabel: titleizeToken(
      readFirstString(sources, [
        "kind",
        "eventType",
        "action",
        "type",
        "data.eventType",
      ]) ?? "entry"
    ),
    occurredAt: readFirstString(sources, [
      "eventAt",
      "createdAt",
      "timestamp",
      "occurredAt",
      "updatedAt",
      "recordedAt",
      "data.eventAt",
      "data.createdAt",
      "data.timestamp",
    ]),
    rawPayload: safeJsonStringify(payload ?? root),
    status: formatTimelineStatus(previousStatus, nextStatus),
    summary: readFirstString(payloadSources, [
      "summary",
      "message",
      "note",
      "updateText",
      "description",
      "reason",
    ]),
  }
}

function countStatus(
  rows:
    | Array<{ _count: number; incidentStatus: string }>
    | undefined,
  statuses: string[]
) {
  if (!rows?.length) {
    return 0
  }

  const allowed = new Set(statuses.map((status) => normalizeToken(status)))
  return rows.reduce((sum, row) => {
    return allowed.has(normalizeToken(row.incidentStatus))
      ? sum + Number(row._count || 0)
      : sum
  }, 0)
}

function severityTone(severity: string | null | undefined) {
  const normalized = normalizeToken(severity)

  if (normalized === "critical") {
    return "bg-destructive/12 text-destructive"
  }

  if (normalized === "severe") {
    return "bg-chart-4/14 text-foreground"
  }

  if (normalized === "moderate") {
    return "bg-primary/10 text-primary"
  }

  return "bg-secondary text-secondary-foreground"
}

function lifecycleTone(status: string | null | undefined) {
  const normalized = normalizeToken(status)

  if (normalized === "resolved" || normalized === "closed") {
    return "bg-chart-3/14 text-foreground"
  }

  if (normalized === "active" || normalized === "dispatched") {
    return "bg-chart-4/14 text-foreground"
  }

  return "bg-primary/10 text-primary"
}

function filterAuthorityIncidents(
  incidents: OperatorIncidentRecord[],
  search: AuthorityDeskSearch | AuthorityReportsSearch
) {
  const query = normalizeToken(search.q)

  return incidents.filter((incident) => {
    if (
      search.incidentStatus
      && search.incidentStatus !== "all"
      && normalizeToken(
        incident.incidentLifecycle?.incidentStatus ?? incident.incidentStatus ?? incident.status
      ) !== normalizeToken(search.incidentStatus)
    ) {
      return false
    }

    if (
      "severity" in search
      && search.severity
      && search.severity !== "all"
      && normalizeToken(incident.severity) !== normalizeToken(search.severity)
    ) {
      return false
    }

    if (
      "jurisdictionId" in search
      && search.jurisdictionId
      && normalizeToken(search.jurisdictionId) !== "all"
      && incident.jurisdiction?.id !== search.jurisdictionId
    ) {
      return false
    }

    if (
      "reportKind" in search
      && search.reportKind
      && search.reportKind !== "all"
      && normalizeToken(incident.reportKind) !== normalizeToken(search.reportKind)
    ) {
      return false
    }

    if (!query) {
      return true
    }

    const haystack = normalizeToken(
      [
        incident.id,
        incident.type,
        incident.reportKind,
        incident.description,
        incident.locationDescription,
        incident.severity,
        incident.stickerId,
        incident.vehicle?.plateNumber,
        incident.contact?.name,
        incident.jurisdiction?.name,
      ]
        .filter(Boolean)
        .join(" ")
    )

    return haystack.includes(query)
  })
}

function buildAuthorityWorkspaceScopeFromSession(
  session: OperatorResolvedSession
): AuthorityWorkspaceScope {
  if (!canAccessAuthorityWorkspace(session)) {
    throw new Error(
      "Authority access is limited to authority-assigned sessions or global portal admins."
    )
  }

  if (session.actorType === "authority") {
    return {
      actorLabel:
        session.displayName
          || session.profile.fullName
          || session.profile.email
          || "Authority operator",
      kind: session.isPortalAdmin ? "global" : "authority",
      organizationId: session.organizationId ?? session.profile.organizationId ?? undefined,
      organizationName:
        session.organizationName
          || session.profile.organizationName
          || session.profile.organization?.name
          || null,
      permissions: session.permissions,
      updatedBy:
        session.profile.fullName
          || session.profile.email
          || session.displayName
          || "Authority desk",
    }
  }

  return {
    actorLabel:
      session.displayName
        || session.profile.name
        || session.profile.email
        || "Portal admin",
    kind: "global",
    organizationName: null,
    permissions: session.permissions,
    updatedBy:
      session.profile.name || session.profile.email || session.displayName || "Portal admin",
  }
}

async function resolveAuthorityWorkspaceScope(
  shellSessionState?: ReturnType<typeof useOptionalOperatorShellSession>
) {
  if (shellSessionState?.status === "error") {
    throw new Error(shellSessionState.message)
  }

  if (shellSessionState?.status === "ready") {
    return buildAuthorityWorkspaceScopeFromSession(shellSessionState.session)
  }

  const session = await resolveOperatorSession()

  return buildAuthorityWorkspaceScopeFromSession(session)
}

function deriveAuthorityJurisdictions(incidents: OperatorIncidentRecord[]) {
  const jurisdictionMap = new Map<string, AuthorityJurisdictionSummary>()

  for (const incident of incidents) {
    const jurisdictionId = incident.jurisdiction?.id
    const jurisdictionName = incident.jurisdiction?.name

    if (!jurisdictionId || !jurisdictionName || jurisdictionMap.has(jurisdictionId)) {
      continue
    }

    jurisdictionMap.set(jurisdictionId, {
      id: jurisdictionId,
      name: jurisdictionName,
    })
  }

  return [...jurisdictionMap.values()].sort((left, right) => {
    return left.name.localeCompare(right.name)
  })
}

async function loadAuthorityBundle(
  mode: "desk" | "reports",
  shellSessionState?: ReturnType<typeof useOptionalOperatorShellSession>
) {
  const scope = await resolveAuthorityWorkspaceScope(shellSessionState)
  const organizationId = scope.kind === "authority" ? scope.organizationId : undefined

  const jurisdictionsPromise = organizationId
    ? apiClient.getOrganizationJurisdictions(organizationId)
    : Promise.resolve(null)

  const incidentPromise = apiClient.getOperatorIncidents({
    limit: 120,
  })
  const statsPromise = apiClient.getOperatorIncidentStats(organizationId)

  const analyticsPromise = hasAnalyticsAccess(scope.permissions) && mode === "desk"
    ? Promise.all([
        apiClient.getOperatorPerformance(organizationId),
        apiClient.getOperatorResponseTimes(120),
        apiClient.getOperatorBreakdown(organizationId),
      ])
    : Promise.resolve([null, null, null] as const)

  const [jurisdictions, incidents, stats, analytics] = await Promise.all([
    jurisdictionsPromise,
    incidentPromise,
    statsPromise,
    analyticsPromise,
  ])

  return {
    breakdown: analytics[2]?.data ?? null,
    incidents: incidents.data ?? [],
    jurisdictions:
      jurisdictions?.data?.length
        ? jurisdictions.data
        : deriveAuthorityJurisdictions(incidents.data ?? []),
    performance: analytics[0]?.data ?? null,
    responseTimes: analytics[1]?.data ?? [],
    scope,
    stats: stats.data ?? null,
  } satisfies AuthorityCoreBundle
}

async function loadAuthorityDetail(incidentId: number) {
  const [detail, timeline, evidence] = await Promise.all([
    apiClient.getOperatorIncident(incidentId),
    apiClient.getOperatorIncidentTimeline(incidentId),
    apiClient.getOperatorIncidentEvidence(incidentId),
  ])

  return {
    detail: detail.data,
    evidence: evidence.data,
    timeline: (timeline.data ?? []) as AuthorityDetailBundle["timeline"],
  } satisfies AuthorityDetailBundle
}

function AuthorityWorkspaceLoadingSkeleton({
  mode,
}: {
  mode: "desk" | "reports"
}) {
  return (
    <div className="flex flex-col gap-5" data-testid="authority-workspace-skeleton">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`authority-metric-skeleton-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-8 w-16" />
            <Skeleton className="mt-3 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-4/5" />
          </div>
        ))}
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <OperatorPanel
          title="Authority queue"
          description="Filter the visible queue by search, severity, lifecycle, and jurisdiction."
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`authority-filter-skeleton-${index}`} className="grid gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-52" />
              <Skeleton className="h-10 w-28 rounded-2xl" />
            </div>
            <div className="grid gap-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`authority-row-skeleton-${index}`}
                  className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="mt-3 h-6 w-40" />
                      <Skeleton className="mt-3 h-4 w-full" />
                      <Skeleton className="mt-2 h-4 w-3/4" />
                    </div>
                    <div className="flex gap-2">
                      <Skeleton className="h-7 w-20 rounded-full" />
                      <Skeleton className="h-7 w-24 rounded-full" />
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 md:grid-cols-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </OperatorPanel>

        <OperatorPanel
          title={
            mode === "desk"
              ? "Incident detail and live actions"
              : "Evidence and reporting detail"
          }
          description={
            mode === "desk"
              ? "Review status, notes, assignments, and response timing for the selected incident."
              : "Review the selected incident's timeline, evidence, and reporting details."
          }
        >
          <div className="grid gap-4">
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-3 h-7 w-44" />
              <Skeleton className="mt-3 h-4 w-36" />
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={`authority-detail-summary-${index}`}
                    className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                  >
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="mt-3 h-5 w-32" />
                  </div>
                ))}
              </div>
            </div>

            {mode === "desk" ? (
              <>
                <div className="grid gap-3 md:grid-cols-2">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton
                      key={`authority-status-action-${index}`}
                      className="h-10 w-full rounded-2xl"
                    />
                  ))}
                </div>
                <div className="grid gap-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-28 w-full rounded-2xl" />
                </div>
                <Skeleton className="h-10 w-28 rounded-2xl" />
              </>
            ) : null}

            <div className="grid gap-3">
              {Array.from({ length: mode === "desk" ? 3 : 2 }).map((_, index) => (
                <div
                  key={`authority-detail-card-${index}`}
                  className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                >
                  <Skeleton className="h-3 w-28" />
                  <div className="mt-3 grid gap-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-10 w-full rounded-2xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </OperatorPanel>
      </section>
    </div>
  )
}

export function AuthorityWorkspace({
  incidentId,
  mode,
  onSearchChange,
  onSelectIncident,
  search,
}: AuthorityWorkspaceProps) {
  const shellSessionState = useOptionalOperatorShellSession()
  const [bundle, setBundle] = useState<AuthorityCoreBundle | null>(null)
  const [detail, setDetail] = useState<AuthorityDetailBundle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isMutating, setIsMutating] = useState(false)
  const [liveStatus, setLiveStatus] = useState<"connected" | "idle" | "reconnecting">(
    "idle"
  )
  const [noteDraft, setNoteDraft] = useState("")
  const deferredSearch = useDeferredValue(search.q ?? "")

  useEffect(() => {
    let cancelled = false

    setIsLoading(true)
    setError(null)

    if (shellSessionState?.status === "loading") {
      setBundle(null)
      return () => {
        cancelled = true
      }
    }

    loadAuthorityBundle(mode, shellSessionState)
      .then((nextBundle) => {
        if (cancelled) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setError(null)
        })
      })
      .catch((nextError: unknown) => {
        if (cancelled) {
          return
        }

        setError(
          nextError instanceof Error
            ? nextError.message
            : "Authority data could not be loaded."
        )
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [mode, shellSessionState])

  const filteredIncidents = useMemo<OperatorIncidentRecord[]>(() => {
    if (!bundle) {
      return []
    }

    return [...filterAuthorityIncidents(bundle.incidents, {
      ...search,
      q: deferredSearch,
    })].sort((left, right) => {
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    })
  }, [bundle, deferredSearch, search])

  const activeIncidentId = incidentId ?? filteredIncidents[0]?.id ?? null

  useEffect(() => {
    if (!activeIncidentId) {
      setDetail(null)
      setDetailError(null)
      return
    }

    let cancelled = false
    setDetailError(null)

    loadAuthorityDetail(activeIncidentId)
      .then((nextDetail) => {
        if (!cancelled) {
          startTransition(() => {
            setDetail(nextDetail)
          })
        }
      })
      .catch((nextError: unknown) => {
        if (!cancelled) {
          setDetailError(
            nextError instanceof Error
              ? nextError.message
              : "Incident detail could not be loaded."
          )
        }
      })

    return () => {
      cancelled = true
    }
  }, [activeIncidentId])

  useEffect(() => {
    if (mode !== "desk" || typeof EventSource === "undefined") {
      return
    }

    const source = apiClient.createIncidentStream()

    const handleConnected = (event: Event) => {
      const messageEvent = event as MessageEvent<string>

      try {
        const parsed = parseOperatorIncidentStreamEvent("connected", messageEvent.data)
        if (parsed) {
          setLiveStatus("connected")
        }
      } catch {
        setLiveStatus("connected")
      }
    }

    const handleIncidentUpdate = (eventType: "incident_created" | "incident_updated") =>
      async (event: Event) => {
        const messageEvent = event as MessageEvent<string>

        try {
          const parsed = parseOperatorIncidentStreamEvent(eventType, messageEvent.data)
          if (!parsed) {
            return
          }

          setLiveStatus("connected")
          setIsRefreshing(true)
          const [nextBundle, nextDetail] = await Promise.all([
            loadAuthorityBundle(mode, shellSessionState),
            activeIncidentId ? loadAuthorityDetail(activeIncidentId) : Promise.resolve(null),
          ])
          startTransition(() => {
            setBundle(nextBundle)
            if (nextDetail) {
              setDetail(nextDetail)
            }
          })
        } catch {
          setLiveStatus("reconnecting")
        } finally {
          setIsRefreshing(false)
        }
      }

    source.addEventListener("connected", handleConnected)
    source.addEventListener("incident_created", handleIncidentUpdate("incident_created"))
    source.addEventListener("incident_updated", handleIncidentUpdate("incident_updated"))
    source.onerror = () => {
      setLiveStatus("reconnecting")
    }

    return () => {
      source.close()
    }
  }, [activeIncidentId, mode, shellSessionState])

  async function refresh() {
    setIsRefreshing(true)
    setError(null)

    try {
      const nextBundle = await loadAuthorityBundle(mode, shellSessionState)
      startTransition(() => {
        setBundle(nextBundle)
      })

      if (activeIncidentId) {
        const nextDetail = await loadAuthorityDetail(activeIncidentId)
        startTransition(() => {
          setDetail(nextDetail)
        })
      }
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Authority data could not be refreshed."
      )
    } finally {
      setIsRefreshing(false)
    }
  }

  async function updateStatus(nextStatus: string) {
    if (!activeIncidentId || !bundle) {
      return
    }

    setIsMutating(true)
    setError(null)

    try {
      await apiClient.updateOperatorIncidentStatus(activeIncidentId, {
        incidentStatus: nextStatus,
        updateText: `Status set to ${titleizeToken(nextStatus)} in the authority desk`,
        updatedBy: bundle.scope.updatedBy,
      })
      await refresh()
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Incident status could not be updated."
      )
    } finally {
      setIsMutating(false)
    }
  }

  async function addNote() {
    if (!activeIncidentId || !bundle || !noteDraft.trim()) {
      return
    }

    setIsMutating(true)
    setError(null)

    try {
      await apiClient.createOperatorIncidentNote(activeIncidentId, {
        actionType: "operations_note",
        updateText: noteDraft.trim(),
        updatedBy: bundle.scope.updatedBy,
      })
      setNoteDraft("")
      await refresh()
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Incident note could not be created."
      )
    } finally {
      setIsMutating(false)
    }
  }

  const selectedIncident =
    detail?.detail
    ?? bundle?.incidents.find((incident) => incident.id === activeIncidentId)
    ?? null
  const scopeMessage =
    bundle?.scope.kind === "global"
      ? `${bundle.scope.actorLabel} is viewing Authority incidents across visible organizations with global portal access.`
      : null

  return (
    <>
      {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
      {scopeMessage ? <OperatorStatusMessage>{scopeMessage}</OperatorStatusMessage> : null}
      {!bundle && isLoading ? (
        <AuthorityWorkspaceLoadingSkeleton mode={mode} />
      ) : null}

      {bundle ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Open incidents"
              value={countStatus(bundle.stats?.by_incident_status, [
                "reported",
                "triaged",
                "dispatched",
                "active",
              ])}
              detail={
                bundle.scope.kind === "global"
                  ? "Global authority access is active across visible organizations."
                  : `${bundle.jurisdictions.length} jurisdictions in current authority scope.`
              }
            />
            <OperatorMetricCard
              label="Recent 24h"
              value={bundle.stats?.recent_24h ?? 0}
              detail="Incident summaries reflect the same scope shown in the authority workspace."
            />
            <OperatorMetricCard
              label="Resolution rate"
              value={
                bundle.performance?.resolutionRate !== undefined
                  ? `${bundle.performance.resolutionRate}%`
                  : "Authority scoped"
              }
              detail={
                hasAnalyticsAccess(bundle.scope.permissions)
                  ? `${bundle.performance?.resolvedIncidents ?? 0} resolved incidents in the loaded analytics window.`
                  : "Analytics cards are hidden for roles without analytics access."
              }
            />
            <OperatorMetricCard
              label="Live feed"
              value={mode === "desk" ? titleizeToken(liveStatus) : "Read view"}
              detail={
                mode === "desk"
                  ? "Live updates refresh the workspace when new incident activity arrives."
                  : "Evidence review stays focused on the selected incident and report history."
              }
            />
          </section>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <OperatorPanel
              title="Authority queue"
              description="Filter the visible queue by search, severity, lifecycle, and jurisdiction."
            >
              <div className="flex flex-col gap-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Search</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={search.q ?? ""}
                      onChange={(event) => onSearchChange({ q: event.target.value || undefined })}
                      placeholder="Incident, tag, plate, or jurisdiction"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Lifecycle</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={search.incidentStatus ?? "all"}
                      onChange={(event) =>
                        onSearchChange({
                          incidentStatus:
                            event.target.value === "all"
                              ? undefined
                              : (event.target.value as AuthorityDeskSearch["incidentStatus"]),
                        })
                      }
                    >
                      {AUTHORITY_INCIDENT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status === "all" ? "All statuses" : titleizeToken(status)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {mode === "desk" ? (
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="text-muted-foreground">Severity</span>
                      <select
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={"severity" in search ? search.severity ?? "all" : "all"}
                        onChange={(event) =>
                          onSearchChange({
                            severity:
                              event.target.value === "all"
                                ? undefined
                                : (event.target.value as AuthorityDeskSearch["severity"]),
                          })
                        }
                      >
                        {AUTHORITY_SEVERITIES.map((severity) => (
                          <option key={severity} value={severity}>
                            {severity === "all" ? "All severities" : titleizeToken(severity)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="text-muted-foreground">Report kind</span>
                      <select
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={"reportKind" in search ? search.reportKind ?? "all" : "all"}
                        onChange={(event) =>
                          onSearchChange({
                            reportKind:
                              event.target.value === "all"
                                ? undefined
                                : (event.target.value as AuthorityReportsSearch["reportKind"]),
                          })
                        }
                      >
                        {AUTHORITY_REPORT_KINDS.map((kind) => (
                          <option key={kind} value={kind}>
                            {kind === "all" ? "All report kinds" : titleizeToken(kind)}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {mode === "desk" ? (
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="text-muted-foreground">Jurisdiction</span>
                      <select
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={
                          "jurisdictionId" in search
                            ? search.jurisdictionId ?? "all"
                            : "all"
                        }
                        onChange={(event) =>
                          onSearchChange({
                            jurisdictionId:
                              event.target.value === "all"
                                ? undefined
                                : event.target.value,
                          })
                        }
                      >
                        <option value="all">All jurisdictions</option>
                        {bundle.jurisdictions.map((jurisdiction) => (
                          <option key={jurisdiction.id} value={jurisdiction.id}>
                            {jurisdiction.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {filteredIncidents.length} incidents match the current authority filters.
                  </p>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    disabled={isRefreshing}
                    className="rounded-2xl border border-border px-3 py-2 text-sm font-medium hover:bg-background"
                  >
                    {isRefreshing ? "Refreshing…" : "Refresh"}
                  </button>
                </div>

                {filteredIncidents.length === 0 ? (
                  <OperatorEmptyState
                    title="No incidents match the current authority filters."
                    message="Try adjusting the current filters to review another incident."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredIncidents.map((incident) => {
                      const isSelected = incident.id === activeIncidentId
                      const incidentStatus =
                        incident.incidentLifecycle?.incidentStatus
                        ?? incident.incidentStatus
                        ?? incident.status

                      return (
                        <button
                          key={incident.id}
                          type="button"
                          onClick={() => onSelectIncident(incident.id)}
                          className={cn(
                            "rounded-3xl border px-4 py-4 text-left transition-colors",
                            isSelected
                              ? "border-primary/50 bg-primary/8"
                              : "border-border/70 bg-background/70 hover:border-primary/25"
                          )}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm text-muted-foreground">
                                Incident #{incident.id}
                              </p>
                              <h3 className="text-lg font-semibold">
                                {titleizeToken(incident.type || incident.reportKind || "incident")}
                              </h3>
                              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                                {incident.description
                                  || incident.locationDescription
                                  || "No incident summary has been recorded yet."}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span
                                className={cn(
                                  "rounded-full px-3 py-1 text-xs font-medium",
                                  severityTone(incident.severity)
                                )}
                              >
                                {titleizeToken(incident.severity)}
                              </span>
                              <span
                                className={cn(
                                  "rounded-full px-3 py-1 text-xs font-medium",
                                  lifecycleTone(incidentStatus)
                                )}
                              >
                                {titleizeToken(incidentStatus)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-4 grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
                            <span>
                              Jurisdiction: {incident.jurisdiction?.name || "Organization-wide"}
                            </span>
                            <span>
                              Reported: {formatRelativeTime(incident.createdAt)}
                            </span>
                            <span>
                              Subject:{" "}
                              {incident.subjectAttribution?.label
                                || titleizeToken(incident.subjectType)}
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </OperatorPanel>

            <OperatorPanel
              title={
                mode === "desk"
                  ? "Incident detail and live actions"
                  : "Evidence and reporting detail"
              }
              description={
                mode === "desk"
                  ? "Review status, notes, assignments, and response timing for the selected incident."
                  : "Review the selected incident's timeline, evidence, and reporting details."
              }
            >
              {!selectedIncident ? (
                <OperatorEmptyState
                  title="Select an authority incident."
                  message="Choose an incident from the queue to review details here."
                />
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Incident #{selectedIncident.id}
                        </p>
                        <h3 className="text-xl font-semibold">
                          {titleizeToken(
                            selectedIncident.type || selectedIncident.reportKind || "incident"
                          )}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          {selectedIncident.location?.name
                            || selectedIncident.locationDescription
                            || "No location label returned"}
                        </p>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDateTime(selectedIncident.createdAt)}
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Jurisdiction
                        </div>
                        <div className="mt-2 font-medium">
                          {selectedIncident.jurisdiction?.name || "Organization-wide"}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Dispatch picture
                        </div>
                        <div className="mt-2 font-medium">
                          {selectedIncident.dispatchSummary?.active ?? 0} active /{" "}
                          {selectedIncident.dispatchSummary?.total ?? 0} total
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Reporting language
                        </div>
                        <div className="mt-2 font-medium">
                          {formatPreferredLanguage(selectedIncident.preferredLanguage)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {mode === "desk" ? (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        {AUTHORITY_INCIDENT_STATUSES.filter(
                          (status) => status !== "all"
                        ).map((status) => (
                          <button
                            key={status}
                            type="button"
                            disabled={isMutating}
                            onClick={() => void updateStatus(status)}
                            className={cn(
                              "rounded-2xl border border-border px-3 py-2 text-left text-sm font-medium hover:bg-background",
                              normalizeToken(
                                selectedIncident.incidentLifecycle?.incidentStatus
                                  ?? selectedIncident.incidentStatus
                                  ?? selectedIncident.status
                              ) === normalizeToken(status)
                                ? "border-primary/45 bg-primary/8"
                                : "bg-background/70"
                            )}
                          >
                            Set {titleizeToken(status)}
                          </button>
                        ))}
                      </div>

                      <label className="flex flex-col gap-2 text-sm">
                        <span className="text-muted-foreground">Operations note</span>
                        <textarea
                          className="min-h-28 rounded-2xl border border-border bg-background px-3 py-2"
                          value={noteDraft}
                          onChange={(event) => setNoteDraft(event.target.value)}
                          placeholder="Record dispatch or supervision context."
                        />
                      </label>
                      <button
                        type="button"
                        disabled={isMutating || !noteDraft.trim()}
                        onClick={() => void addNote()}
                        className="rounded-2xl border border-border px-3 py-2 text-sm font-medium hover:bg-background"
                      >
                        {isMutating ? "Saving…" : "Add note"}
                      </button>
                    </>
                  ) : null}

                  {detailError ? (
                    <OperatorStatusMessage tone="warning">
                      {detailError}
                    </OperatorStatusMessage>
                  ) : null}

                  <div className="grid gap-4">
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        Assignments
                      </div>
                      <div className="mt-3 grid gap-3">
                        {(selectedIncident.assignments ?? []).length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No assignment rows were returned for this incident.
                          </p>
                        ) : (
                          (selectedIncident.assignments ?? []).map((assignment) => (
                            <div
                              key={assignment.id}
                              className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="font-medium">
                                  {titleizeToken(assignment.agencyKind)} assignment
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {titleizeToken(assignment.status)}
                                </span>
                              </div>
                              <div className="mt-2 text-sm leading-6 text-muted-foreground">
                                {assignment.authority?.fullName || "Unassigned authority"} •{" "}
                                {assignment.jurisdiction?.name || "No jurisdiction"} •{" "}
                                {assignment.priority
                                  ? titleizeToken(assignment.priority)
                                  : "Priority not recorded"}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {mode === "desk" && bundle.performance ? (
                      <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Response clocks
                        </div>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                            <div className="text-sm text-muted-foreground">
                              Average response time
                            </div>
                            <div className="mt-2 text-xl font-semibold">
                              {bundle.performance.averageResponseTime ?? "—"} min
                            </div>
                          </div>
                          <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                            <div className="text-sm text-muted-foreground">
                              Golden-hour compliance
                            </div>
                            <div className="mt-2 text-xl font-semibold">
                              {bundle.performance.goldenHourComplianceRate ?? 0}%
                            </div>
                          </div>
                        </div>
                        {bundle.responseTimes.length > 0 ? (
                          <div className="mt-3 grid gap-2">
                            {bundle.responseTimes.slice(0, 3).map((row) => (
                              <div
                                key={row.id}
                                className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                              >
                                Incident #{row.id} • dispatch {row.clocks.dispatchMinutes ?? "—"} min
                                • acknowledge {row.clocks.acknowledgementMinutes ?? "—"} min
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {mode === "reports" ? (
                      <>
                        <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Evidence package
                          </div>
                          <div className="mt-2 font-medium">
                            {detail?.evidence?.checksum
                              ? `Checksum ${detail.evidence.checksum}`
                              : "No checksum returned"}
                          </div>
                          <div className="mt-3 text-sm leading-6 text-muted-foreground">
                            Photos: {selectedIncident.incidentMedia?.length ?? 0} • Voice notes:{" "}
                            {selectedIncident.voiceNotes?.length ?? 0} • Chain of custody:{" "}
                            {selectedIncident.chainOfCustody?.length ?? 0}
                          </div>
                        </div>
                        <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Voice note recordings
                          </div>
                          <div className="mt-3 grid gap-3">
                            {(selectedIncident.voiceNotes ?? []).length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No voice note recordings were returned for this incident.
                              </p>
                            ) : (
                              (selectedIncident.voiceNotes ?? []).map((voiceNote, index) => {
                                const label =
                                  voiceNote.fileName?.trim() || `Voice note ${index + 1}`
                                const fileSize = formatFileSize(voiceNote.fileSize)

                                return (
                                  <div
                                    key={voiceNote.id}
                                    className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="font-medium">{label}</div>
                                      <span className="text-xs text-muted-foreground">
                                        {formatDateTime(voiceNote.createdAt)}
                                      </span>
                                    </div>
                                    <div className="mt-2 text-sm text-muted-foreground">
                                      {voiceNote.mimeType || "MIME type not recorded"}
                                      {fileSize ? ` • ${fileSize}` : ""}
                                    </div>
                                    {voiceNote.transcriptionStatus
                                      || voiceNote.transcriptLanguage ? (
                                        <div className="mt-1 text-xs text-muted-foreground">
                                          Transcript:{" "}
                                          {voiceNote.transcriptionStatus
                                            ? titleizeToken(voiceNote.transcriptionStatus)
                                            : "Unknown"}
                                          {voiceNote.transcriptLanguage
                                            ? ` (${voiceNote.transcriptLanguage.toUpperCase()})`
                                            : ""}
                                        </div>
                                      ) : null}
                                    <audio
                                      className="mt-3 w-full"
                                      controls
                                      preload="none"
                                      aria-label={`${label} playback`}
                                    >
                                      <source
                                        src={voiceNote.url}
                                        type={voiceNote.mimeType ?? undefined}
                                      />
                                      Your browser does not support audio playback.
                                    </audio>
                                  </div>
                                )
                              })
                            )}
                          </div>
                        </div>
                        <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Timeline
                          </div>
                          <div className="mt-3 grid gap-3">
                            {(detail?.timeline ?? []).length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No timeline entries were returned for this incident.
                              </p>
                            ) : (
                              (detail?.timeline ?? []).map((entry, index) => {
                                const timelineEntry = describeTimelineEntry(entry)
                                return (
                                  <div
                                    key={`${entry.kind ?? "timeline"}-${index}`}
                                    className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="font-medium">
                                        {timelineEntry.eventLabel}
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        {timelineEntry.occurredAt
                                          ? formatDateTime(timelineEntry.occurredAt)
                                          : "Time unavailable"}
                                      </span>
                                    </div>
                                    <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
                                      {timelineEntry.actor ? (
                                        <div>Actor: {timelineEntry.actor}</div>
                                      ) : null}
                                      {timelineEntry.status ? (
                                        <div>Status: {timelineEntry.status}</div>
                                      ) : null}
                                      {timelineEntry.area ? (
                                        <div>Area: {timelineEntry.area}</div>
                                      ) : null}
                                      {timelineEntry.summary ? (
                                        <div>{timelineEntry.summary}</div>
                                      ) : null}
                                    </div>
                                    <details className="mt-2 text-xs text-muted-foreground">
                                      <summary className="cursor-pointer select-none font-medium">
                                        Raw payload
                                      </summary>
                                      <pre className="mt-2 max-h-56 overflow-auto rounded-xl border border-border/70 bg-muted/30 p-2 text-[11px] leading-5 text-muted-foreground">
                                        {timelineEntry.rawPayload}
                                      </pre>
                                    </details>
                                  </div>
                                )
                              })
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Recent incident notes
                        </div>
                        <div className="mt-3 grid gap-3">
                          {(selectedIncident.updates ?? []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No update rows were returned for this incident.
                            </p>
                          ) : (
                            (selectedIncident.updates ?? []).slice(0, 5).map((update) => (
                              <div
                                key={update.id}
                                className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                              >
                                <div className="font-medium">
                                  {update.updatedBy || "Authority note"}
                                </div>
                                <div className="mt-1 text-sm text-muted-foreground">
                                  {update.updateText || "No note body returned"}
                                </div>
                                <div className="mt-2 text-xs text-muted-foreground">
                                  {formatDateTime(update.createdAt)}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </OperatorPanel>
          </section>
        </>
      ) : null}
    </>
  )
}
